import { useState } from 'react'

import { dragCoef, fallStep, fallTime, ru } from '../../lib/physics'
import { Choice, Predict, useFrames } from './common'

/**
 * Падение листа и блокнота (урок 33): лист плашмя отстаёт только из-за воздуха.
 * Положи лист на блокнот или убери воздух — падают одинаково, масса не важна.
 */

type Лист = 'плашмя' | 'ребром' | 'комок' | 'на блокноте'

const H = 1.5 // м — высота стола и вытянутой руки
const TOP = 40
const FLOOR = 270
const PX_PER_M = (FLOOR - TOP) / H
const SLOW = 3 // замедление: настоящее падение блокнота — полсекунды

const M_ЛИСТ = 0.005
const M_БЛОКНОТ = 0.2
const C_БЛОКНОТ = dragCoef(0.15 * 0.21)
const C_ЛИСТ: Record<Лист, number> = {
  плашмя: dragCoef(0.21 * 0.297),
  ребром: dragCoef(0.21 * 0.002),
  комок: dragCoef(Math.PI * 0.03 ** 2, 0.5),
  'на блокноте': 0,
}

interface Тело {
  y: number
  v: number
  t: number | null // время приземления, с
}

const старт = (): { блокнот: Тело; лист: Тело; t: number } => ({
  блокнот: { y: 0, v: 0, t: null },
  лист: { y: 0, v: 0, t: null },
  t: 0,
})

export default function FallLab() {
  const [лист, setЛист] = useState<Лист>('плашмя')
  const [воздух, setВоздух] = useState(true)
  const [летят, setЛетят] = useState(false)
  const [s, setS] = useState(старт)

  const вместе = лист === 'на блокноте'
  const cБлокнот = воздух ? C_БЛОКНОТ : 0
  const cЛист = воздух ? C_ЛИСТ[лист] : 0
  const mБлокнот = вместе ? M_БЛОКНОТ + M_ЛИСТ : M_БЛОКНОТ

  useFrames((dt) => {
    if (!летят) return
    setS((p) => {
      const шаг = dt / SLOW
      const t = p.t + шаг
      const двинуть = (тело: Тело, m: number, c: number): Тело => {
        if (тело.t !== null) return тело
        let { y, v } = тело
        const n = 10
        for (let i = 0; i < n; i++) [y, v] = fallStep(y, v, m, c, шаг / n)
        return y >= H ? { y: H, v: 0, t } : { y, v, t: null }
      }
      const блокнот = двинуть(p.блокнот, mБлокнот, cБлокнот)
      const листТело = вместе ? блокнот : двинуть(p.лист, M_ЛИСТ, cЛист)
      if (блокнот.t !== null && листТело.t !== null) setЛетят(false)
      return { блокнот, лист: листТело, t }
    })
  })

  const сброс = () => {
    setЛетят(false)
    setS(старт())
  }
  const отпустить = () => {
    setS(старт())
    setЛетят(true)
  }

  // правильный ответ «что упадёт первым» — по той же модели
  const tБ = fallTime(H, mБлокнот, cБлокнот)
  const tЛ = вместе ? tБ : fallTime(H, M_ЛИСТ, cЛист)
  const первым = Math.abs(tБ - tЛ) < 0.05 ? 2 : tБ < tЛ ? 0 : 1
  const приземлились = s.блокнот.t !== null && s.лист.t !== null

  const yБ = TOP + s.блокнот.y * PX_PER_M
  const yЛ = TOP + s.лист.y * PX_PER_M
  const качание = летят && лист === 'плашмя' && воздух ? Math.sin(s.t * 14) * 10 : 0

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 300" role="img" aria-label="Падение листа и блокнота">
        {!воздух && <rect x={20} y={10} width={320} height={FLOOR - 10} rx={14} className="lab-vacuum" />}
        <line x1={10} y1={FLOOR + 1} x2={350} y2={FLOOR + 1} className="ink" strokeWidth={3} />
        <line x1={20} y1={TOP} x2={340} y2={TOP} className="ink-muted" strokeDasharray="4 4" />
        <text x={24} y={TOP - 6} className="lab-tick">{ru(H, 1)} м</text>
        {/* блокнот */}
        <rect x={vx(вместе) - 30} y={yБ - 14} width={60} height={14} rx={2} className="fill-body ink" strokeWidth={1.5} />
        <text x={vx(вместе)} y={FLOOR + 22} textAnchor="middle" className="lab-label">
          {вместе ? 'блокнот с листом' : 'блокнот'}
        </text>
        {вместе && <rect x={vx(true) - 31} y={yБ - 17} width={62} height={3} className="fill-paper ink" strokeWidth={0.8} />}
        {/* лист */}
        {!вместе && (
          <g transform={`translate(250 ${yЛ}) rotate(${качание})`}>
            {лист === 'плашмя' && <rect x={-32} y={-3} width={64} height={3} className="fill-paper ink" strokeWidth={0.8} />}
            {лист === 'ребром' && <rect x={-1.5} y={-34} width={3} height={34} className="fill-paper ink" strokeWidth={0.8} />}
            {лист === 'комок' && <circle cx={0} cy={-7} r={7} className="fill-paper ink" strokeWidth={0.8} />}
          </g>
        )}
        {!вместе && (
          <text x={250} y={FLOOR + 22} textAnchor="middle" className="lab-label">лист</text>
        )}
        {!воздух && <text x={180} y={28} textAnchor="middle" className="lab-tick">воздух откачан</text>}
      </svg>

      <div className="lab-readout">
        <span>блокнот: {s.блокнот.t === null ? '…' : `${ru(s.блокнот.t, 2)} с`}</span>
        {!вместе && <span>лист: {s.лист.t === null ? '…' : `${ru(s.лист.t, 2)} с`}</span>}
        <span className="muted">время замедлено в {SLOW} раза</span>
      </div>

      <Predict
        question="Что упадёт на пол первым?"
        options={['блокнот', 'лист', 'одновременно']}
        right={первым}
        revealed={приземлились}
        resetKey={`${лист}-${воздух}`}
        explain={
          воздух && первым !== 2
            ? 'Лист отстаёт только из-за сопротивления воздуха: у него большая площадь при маленькой массе.'
            : 'Без воздуха (или когда воздух мешает обоим одинаково) тела любой массы падают одинаково: скорость растёт на g каждую секунду.'
        }
      />

      <div className="lab-sub">Лист</div>
      <Choice
        options={[
          { value: 'плашмя', label: 'плашмя' },
          { value: 'ребром', label: 'ребром' },
          { value: 'комок', label: 'скомкать' },
          { value: 'на блокноте', label: 'положить на блокнот' },
        ]}
        value={лист}
        onChange={(v) => { setЛист(v); сброс() }}
      />
      <div className="lab-sub">Воздух</div>
      <Choice
        options={[
          { value: 1, label: 'есть' },
          { value: 0, label: 'откачать' },
        ]}
        value={воздух ? 1 : 0}
        onChange={(v) => { setВоздух(v === 1); сброс() }}
      />
      <div className="row-wrap" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={отпустить}>✋ Отпустить</button>
      </div>
      <p className="muted small">
        Сила тяжести пропорциональна массе, но и инертность тоже: во сколько раз больше сила, во
        столько раз труднее разогнать. Поэтому без воздуха все тела падают одинаково.
      </p>
    </div>
  )
}

/** Где по горизонтали блокнот: один — слева, с листом — посередине. */
function vx(вместе: boolean): number {
  return вместе ? 180 : 110
}
